import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { PROJECT_STATUSES } from "@/lib/site";

const EMPTY = {
    title: "", slug: "", headline: "", summary: "", description: "", location: "", region: "Greater Lisbon", type: "Residential",
    program: "", homes: null, status: "Exploring", site_area: "", permitted_area: "", licensing_status: "", hero_image: "", hero_is_render: false,
    gallery: [], highlights: [], distances: [], map_query: "", video_url: "", featured: false, visible: true, archived: false, sort: 0, seo_title: "", seo_description: "",
};

const TEXT = [["title", "Title"], ["slug", "URL slug (optional)"], ["headline", "Headline"], ["location", "Location"], ["region", "Region"], ["type", "Type"],
    ["program", "Programme (e.g. 18 homes)"], ["licensing_status", "Licensing status"], ["site_area", "Site area"], ["permitted_area", "Construction area"],
    ["map_query", "Map search (e.g. Sintra, Portugal)"], ["seo_title", "SEO title"]];
const LONG = [["summary", "Summary"], ["description", "Description (blank line = new paragraph)"], ["seo_description", "SEO description"]];
const FLAGS = [["featured", "Featured on homepage"], ["visible", "Publicly visible"], ["archived", "Archived"]];

function useUpload(token) {
    const [busy, setBusy] = useState(false);
    const upload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { "X-Admin-Password": token } });
            return data;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
            return null;
        } finally { setBusy(false); }
    };
    return [upload, busy];
}

function Editor({ initial, client, token, onDone }) {
    const [p, setP] = useState({ ...EMPTY, ...initial });
    const [saving, setSaving] = useState(false);
    const [upload, uploading] = useUpload(token);
    const set = (k) => (e) => setP((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const addMedia = async (e, target) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        for (const f of files) {
            const r = await upload(f);
            if (!r) continue;
            const kind = r.content_type.startsWith("video") ? "video" : "image";
            if (target === "hero") setP((x) => ({ ...x, hero_image: r.url }));
            else if (target === "video") setP((x) => ({ ...x, video_url: r.url }));
            else setP((x) => ({ ...x, gallery: [...x.gallery, { url: r.url, caption: "", is_render: false, kind }] }));
        }
    };
    const setG = (i, k, v) => setP((x) => ({ ...x, gallery: x.gallery.map((g, j) => (j === i ? { ...g, [k]: v } : g)) }));

    const save = async () => {
        setSaving(true);
        const body = { ...p, highlights: (p.highlights || []).map((h) => h.trim()).filter(Boolean), distances: (p.distances || []).map((d) => ({ label: d.label.trim(), value: (d.value || "").trim() })).filter((d) => d.label), homes: p.homes ? Number(p.homes) : null, sort: Number(p.sort) || 0 };
        try {
            if (p.id) await client.put(`/admin/projects/${p.id}`, body);
            else await client.post("/admin/projects", body);
            toast.success("Project saved");
            onDone();
        } catch (e) {
            toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Save failed — title and status are required");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="admin-project-editor" className="space-y-6 max-w-4xl">
            <button type="button" onClick={onDone} className="inline-flex items-center gap-2 text-sm text-muted_text hover:text-forest"><ArrowLeft className="h-4 w-4" /> Back to projects</button>
            <div className="grid sm:grid-cols-2 gap-4">
                {TEXT.map(([k, l]) => (
                    <label key={k} className="block"><span className="mono-label text-muted_text mb-2 block">{l}</span>
                        <input value={p[k] || ""} onChange={set(k)} className="editorial-input" data-testid={`admin-project-${k}`} /></label>
                ))}
                <label className="block"><span className="mono-label text-muted_text mb-2 block">Status</span>
                    <select value={p.status} onChange={set("status")} className="editorial-input" data-testid="admin-project-status">
                        {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select></label>
                <label className="block"><span className="mono-label text-muted_text mb-2 block">Sort order</span>
                    <input type="number" value={p.sort} onChange={set("sort")} className="editorial-input" data-testid="admin-project-sort" /></label>
            </div>
            {LONG.map(([k, l]) => (
                <label key={k} className="block"><span className="mono-label text-muted_text mb-2 block">{l}</span>
                    <textarea rows={k === "description" ? 6 : 3} value={p[k] || ""} onChange={set(k)} className="editorial-input" data-testid={`admin-project-${k}`} /></label>
            ))}
            <label className="block"><span className="mono-label text-muted_text mb-2 block">Highlights (one per line)</span>
                <textarea rows={5} value={(p.highlights || []).join("\n")} onChange={(e) => setP((x) => ({ ...x, highlights: e.target.value.split("\n") }))} className="editorial-input" data-testid="admin-project-highlights" /></label>
            <label className="block"><span className="mono-label text-muted_text mb-2 block">Drive times (one per line: Place | 10 min)</span>
                <textarea rows={4} value={(p.distances || []).map((d) => (d.value !== undefined ? `${d.label}|${d.value}` : d.label)).join("\n")}
                          onChange={(e) => setP((x) => ({ ...x, distances: e.target.value.split("\n").map((l) => { const [label, ...rest] = l.split("|"); return rest.length ? { label, value: rest.join("|") } : { label }; }) }))}
                          className="editorial-input" data-testid="admin-project-distances" /></label>
            <div className="flex flex-wrap gap-6">
                {FLAGS.map(([k, l]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!p[k]} onChange={set(k)} className="accent-forest" data-testid={`admin-project-${k}`} /> {l}</label>
                ))}
            </div>

            <div className="rounded-2xl border border-hairline p-6 space-y-4">
                <p className="mono-label text-moss">Media {uploading && <Loader2 className="inline h-4 w-4 animate-spin" />}</p>
                <div className="flex flex-wrap items-center gap-4">
                    {p.hero_image && <img src={p.hero_image} alt="" className="h-20 w-32 object-cover rounded-lg" />}
                    <label className="inline-flex items-center gap-2 rounded-full border border-forest/40 px-4 py-2 text-sm cursor-pointer" data-testid="admin-upload-hero">
                        <Upload className="h-4 w-4" /> Hero image<input type="file" accept="image/*" className="hidden" onChange={(e) => addMedia(e, "hero")} /></label>
                    <label className="inline-flex items-center gap-2 rounded-full border border-forest/40 px-4 py-2 text-sm cursor-pointer" data-testid="admin-upload-gallery">
                        <Plus className="h-4 w-4" /> Gallery images/videos<input type="file" multiple accept="image/*,video/mp4,video/webm" className="hidden" onChange={(e) => addMedia(e, "gallery")} /></label>
                    <label className="inline-flex items-center gap-2 rounded-full border border-forest/40 px-4 py-2 text-sm cursor-pointer" data-testid="admin-upload-video">
                        <Upload className="h-4 w-4" /> Feature video<input type="file" accept="video/mp4,video/webm" className="hidden" onChange={(e) => addMedia(e, "video")} /></label>
                </div>
                {p.video_url && <p className="text-xs text-muted_text">Video: {p.video_url} <button type="button" className="underline" onClick={() => setP((x) => ({ ...x, video_url: "" }))}>remove</button></p>}
                <ul className="space-y-2">
                    {p.gallery.map((g, i) => (
                        <li key={g.url + i} className="flex flex-wrap items-center gap-3" data-testid={`admin-gallery-item-${i}`}>
                            {g.kind === "video" ? <span className="h-12 w-20 rounded bg-forest text-cream text-xs flex items-center justify-center">video</span> : <img src={g.url} alt="" className="h-12 w-20 object-cover rounded" />}
                            <input placeholder="Caption" value={g.caption || ""} onChange={(e) => setG(i, "caption", e.target.value)} className="editorial-input !w-56 !py-2" />
                            <button type="button" aria-label="Remove" onClick={() => setP((x) => ({ ...x, gallery: x.gallery.filter((_, j) => j !== i) }))} className="text-muted_text hover:text-forest"><Trash2 className="h-4 w-4" /></button>
                        </li>
                    ))}
                </ul>
            </div>
            <button type="button" onClick={save} disabled={saving || uploading} data-testid="admin-project-save"
                    className="rounded-full bg-forest text-cream px-8 py-3.5 text-sm font-medium disabled:opacity-60">{saving ? "Saving…" : "Save project"}</button>
        </div>
    );
}

export default function AdminProjects({ client, token }) {
    const [items, setItems] = useState(null);
    const [editing, setEditing] = useState(null);
    const load = useCallback(() => { client.get("/admin/projects").then(({ data }) => setItems(data)).catch(() => toast.error("Failed to load projects")); }, [client]);
    useEffect(load, [load]);

    const remove = async (p) => {
        if (!window.confirm(`Permanently delete "${p.title}"?`)) return;
        try { await client.delete(`/admin/projects/${p.id}`); load(); } catch { toast.error("Delete failed"); }
    };

    if (editing) return <Editor initial={editing} client={client} token={token} onDone={() => { setEditing(null); load(); }} />;
    if (!items) return <Loader2 className="h-6 w-6 animate-spin text-forest" />;
    return (
        <div data-testid="admin-projects">
            <button type="button" onClick={() => setEditing({})} data-testid="admin-project-new" className="mb-6 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-sm"><Plus className="h-4 w-4" /> New project</button>
            <ul className="space-y-3">
                {items.map((p) => (
                    <li key={p.id} className="flex items-center gap-4 rounded-2xl bg-white/60 border border-hairline p-4" data-testid={`admin-project-row-${p.slug}`}>
                        {p.hero_image && <img src={p.hero_image} alt="" className="h-14 w-20 object-cover rounded-lg" />}
                        <div className="flex-1 min-w-0">
                            <p className="font-serif text-xl text-forest truncate">{p.title}</p>
                            <p className="text-xs text-muted_text">{p.status} · {p.visible ? "visible" : "hidden"}{p.featured ? " · featured" : ""}{p.archived ? " · archived" : ""}</p>
                        </div>
                        {p.archived && (
                            <button type="button" onClick={() => remove(p)} className="text-muted_text hover:text-forest" aria-label={`Delete ${p.title}`} data-testid={`admin-project-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                        )}
                        <button type="button" onClick={() => setEditing(p)} className="rounded-full border border-forest/40 px-4 py-2 text-sm" data-testid={`admin-project-edit-${p.slug}`}>Edit</button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
